import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useUserStore from "../stores/useUserStore";
import useFleetStore from "../stores/useFleetStore";
import { apiFetchAuthed } from "../apiHelpers";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Panel from "../components/ui/Panel";
import EmptyState from "../components/ui/EmptyState";
import FadeIn from "../components/ui/FadeIn";
import { Skeleton, SkeletonGroup } from "../components/ui/Skeleton";

dayjs.extend(relativeTime);

const getStatusColor = (condition) => {
  switch (condition) {
    case "RTF":
      return "text-green-500";
    case "Flag":
      return "text-yellow-500";
    case "OOC":
      return "text-danger";
    default:
      return "text-primary";
  }
};

const RecentlyTouched = () => {
  const user = useUserStore((state) => state.user);
  const drones = useFleetStore((state) => state.drones);
  const [recentDrones, setRecentDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFleetWide, setIsFleetWide] = useState(false);

  const droneConditions = drones.reduce((acc, d) => {
    acc[d.serialnumber] = d.condition;
    return acc;
  }, {});

  useEffect(() => {
    if (user?.id) {
      apiFetchAuthed(`/maintenance/recent/${user.id}`)
        .then((data) => {
          if (data && data.length > 0) {
            setRecentDrones(data);
            setIsFleetWide(false);
            setLoading(false);
          } else {
            return apiFetchAuthed(`/maintenance/recent/all`)
              .then((fleetData) => {
                setRecentDrones(fleetData || []);
                setIsFleetWide(true);
                setLoading(false);
              });
          }
        })
        .catch((err) => {
          console.error("Error fetching recent drones:", err);
          setLoading(false);
        });
    }
  }, [user]);

  const title = isFleetWide ? "Recent fleet activity" : "Your recent activity";

  if (loading) {
    return (
      <Panel title={title} titleStyle="section" accent="primary" lit>
        <SkeletonGroup label="Loading recent activity" className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </SkeletonGroup>
      </Panel>
    );
  }

  if (recentDrones.length === 0) {
    return (
      <Panel label="Recent activity">
        <FadeIn><EmptyState variant="line">No recent activity</EmptyState></FadeIn>
      </Panel>
    );
  }

  return (
    <Panel title={title} titleStyle="section" accent="primary" lit padding="none" className="overflow-hidden">
      <FadeIn>
      <ul className="max-h-[140px] overflow-y-auto">
        {recentDrones.map((drone, index) => (
          <li
            key={`${drone.drone_serial}-${index}`}
            className="hover:bg-gray-700/30 transition-colors duration-100"
          >
            <Link
              to={`/drone/${drone.drone_serial}`}
              className="flex items-center justify-between gap-2 px-4 py-2.5 whitespace-nowrap min-h-[44px]"
            >
              <div className="flex items-center gap-2.5 min-w-0 overflow-hidden">
                <span className={`${getStatusColor(droneConditions[drone.drone_serial])} font-mono font-bold text-sm flex-shrink-0`}>
                  {drone.drone_serial?.slice(-6)}
                </span>
                <span className="text-on-surface text-sm truncate">
                  {drone.last_action}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isFleetWide && drone.firstname && (
                  <span className="text-on-surface-disabled text-xs">
                    {drone.firstname}
                  </span>
                )}
                <span className="text-on-surface-variant text-xs whitespace-nowrap">
                  {dayjs(drone.last_touched).fromNow()}
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      </FadeIn>
    </Panel>
  );
};

export default RecentlyTouched;
